import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { SampleStatusCell } from "../components/common/DataTable";
import ConfirmModal from "../components/common/ConfirmModal";
import { useAdminApi } from "../hooks/useAdminApi";
import { EventSummary } from "../types/admin";
import { toast } from "react-toastify";

interface EventRow {
	id: number;
	external_id?: string;
	title: string;
	organizer?: string;
	status: "draft" | "published";
	ticket_types_count?: number;
	publication_date_time?: string | null;
}

const EventsPage: React.FC = () => {
	const api = useAdminApi();
	const [filters, setFilters] = useState({
		q: "",
		organizer: "",
		status: "",
	});
	const [rows, setRows] = useState<EventRow[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [modalOpen, setModalOpen] = useState(false);
	const [target, setTarget] = useState<EventRow | null>(null);
	const [desiredStatus, setDesiredStatus] = useState<
		"draft" | "published" | null
	>(null);
	const [actionLoading, setActionLoading] = useState(false);

	const mapEvent = useCallback(
		(e: EventSummary): EventRow => ({
			id: e.id,
			external_id: e.external_id,
			title: e.title,
			organizer: e.organizer?.organizer_name,
			status: e.status,
			ticket_types_count: e.ticket_types_count ?? 0,
			publication_date_time: e.publication_date_time,
		}),
		[]
	);

	const fetchEvents = useCallback(
		async (pageOverride?: number) => {
			setLoading(true);
			setError(null);
			try {
				const res = await api.listEvents({
					title: filters.q || undefined,
					status: filters.status || undefined,
					organizer_id: filters.organizer || undefined,
					page: pageOverride ?? page,
					per_page: pageSize,
				});
				const mapped = res.data.map(mapEvent);
				setRows(mapped);
				setTotal(res.total);
				setPageSize(res.per_page);
			} catch (err) {
				console.warn("Failed to load events", err);
				setError("イベント一覧の取得に失敗しました");
			} finally {
				setLoading(false);
			}
		},
		[api, filters, page, pageSize, mapEvent]
	);

	useEffect(() => {
		fetchEvents();
	}, [fetchEvents]);

	const columns = useMemo(
		() => [
			{ key: "external_id", header: "ID" },
			{ key: "title", header: "イベント名" },
			{ key: "organizer", header: "オーガナイザー" },
			{
				key: "status",
				header: "状態",
				render: (row: EventRow) => <SampleStatusCell status={row.status} />,
			},
			{
				key: "ticket_types_count",
				header: "チケット数",
				render: (row: EventRow) => row.ticket_types_count ?? "-",
			},
			{
				key: "actions",
				header: "操作",
				render: (row: EventRow) => (
					<div className="flex gap-2 text-xs font-semibold">
						<button
							className="text-primary"
							onClick={() => {
								const next = row.status === "draft" ? "published" : "draft";
								setDesiredStatus(next);
								setTarget(row);
								setModalOpen(true);
							}}
						>
							{row.status === "draft" ? "公開する" : "下書きに戻す"}
						</button>
					</div>
				),
			},
		],
		[]
	);

	const handleConfirm = async () => {
		if (!target || !desiredStatus) return;
		setActionLoading(true);
		try {
			const res = await api.updateEventStatus(target.id, desiredStatus);
			const updated = mapEvent((res.data?.data ?? res.data) as EventSummary);
			setRows((prev) =>
				prev.map((row) => (row.id === updated.id ? updated : row))
			);
			toast.success(`ステータスを ${desiredStatus} に更新しました`);
		} catch (err) {
			console.error("Failed to update event status", err);
			toast.error("ステータス更新に失敗しました");
		} finally {
			setActionLoading(false);
			setModalOpen(false);
			setDesiredStatus(null);
			setTarget(null);
		}
	};

	const handleFilter = () => {
		setPage(1);
		fetchEvents(1);
	};

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-xl font-semibold text-ink">Events</h1>
				<p className="text-sm text-muted">
					公開/非公開・販売停止/再開は確認付きで実行
				</p>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="grid gap-3 md:grid-cols-4">
					<input
						className="input"
						placeholder="イベント名"
						value={filters.q}
						onChange={(e) => setFilters({ ...filters, q: e.target.value })}
					/>
					<input
						className="input"
						placeholder="オーガナイザーID"
						value={filters.organizer}
						onChange={(e) =>
							setFilters({ ...filters, organizer: e.target.value })
						}
					/>
					<select
						className="input"
						value={filters.status}
						onChange={(e) => setFilters({ ...filters, status: e.target.value })}
					>
						<option value="">状態</option>
						<option value="draft">draft</option>
						<option value="published">published</option>
					</select>
					<button
						className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
						onClick={handleFilter}
					>
						フィルタ
					</button>
				</div>

				<div className="mt-4">
					<DataTable
						data={rows}
						columns={columns}
						total={total}
						page={page}
						pageSize={pageSize}
						onPageChange={setPage}
						loading={loading}
						error={error}
					/>
				</div>
			</div>

			<ConfirmModal
				open={modalOpen}
				title="公開ステータスを変更しますか？"
				message={target?.title}
				confirmLabel="更新する"
				loading={actionLoading}
				showReason={false}
				requireReason={false}
				onConfirm={handleConfirm}
				onCancel={() => setModalOpen(false)}
			/>
		</div>
	);
};

export default EventsPage;
